import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Lock, Mail, MapPin, User } from 'lucide-react';
import Header from '../components/layout/Header';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../components/auth/AuthContext';
import { countryOptions } from '../services/countries';

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function RegisterPage() {
  const [form, setForm] = useState({ name: '', email: '', country: 'Bénin', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();
  const disabled = !form.name.trim() || !form.email.trim() || form.password.length < 6;

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return setError('Indiquez votre nom complet.');
    if (!isValidEmail(form.email)) return setError('Indiquez une adresse email valide.');
    if (form.password.length < 6) return setError('Le mot de passe doit contenir au moins 6 caractères.');
    setLoading(true);
    setError('');
    try {
      const result = await register({ name: form.name, email: form.email, password: form.password, country: form.country });
      if (!result.ok) {
        setError(result.error);
        return;
      }
      navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <Header showBack backLabel="Créer un compte" onBack={() => navigate('/onboarding')} />
      <main className="flex-1 pt-8 md:max-w-xl md:mx-auto md:w-full">
        <h1 className="text-3xl md:text-4xl font-extrabold text-brand-navy">Bienvenue sur SENTINELLE</h1>
        <p className="mt-3 text-text-muted leading-relaxed">Créez votre espace personnel pour retrouver vos analyses, vos rapports et vos signalements.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
          <AuthField icon={User} label="Nom complet" value={form.name} onChange={(value) => update('name', value)} placeholder="Ex. Kofi Mensah" />
          <AuthField icon={Mail} label="Adresse email" type="email" value={form.email} onChange={(value) => update('email', value)} placeholder="vous@email.com" />
          <CountryField value={form.country} onChange={(value) => update('country', value)} />
          <AuthField icon={Lock} label="Mot de passe" type="password" value={form.password} onChange={(value) => update('password', value)} placeholder="Au moins 6 caractères" />
          {error ? <ErrorMessage message={error} /> : null}
          {loading && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl text-sm text-blue-700">
              <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              Création du compte… La première connexion peut prendre quelques secondes.
            </div>
          )}
          <button type="submit" disabled={disabled || loading} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
            {loading ? (
              <><svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Création…</>
            ) : (
              <>Créer mon compte <ArrowRight className="w-5 h-5" /></>
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-text-muted">Vous avez déjà un compte ? <Link to="/login" className="text-accent-blue font-semibold">Se connecter</Link></p>
      </main>
    </div>
  );
}

function AuthField({ icon: Icon, label, value, onChange, placeholder, type = 'text' }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-text-primary">{label}</span>
      <span className="mt-2 h-14 bg-white border border-gray-200 rounded-xl px-4 flex items-center gap-3 focus-within:border-accent-blue focus-within:ring-2 focus-within:ring-accent-blue/20">
        <Icon className="w-5 h-5 text-text-muted" />
        <input type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className="w-full bg-transparent outline-none text-text-primary placeholder-text-subtle" />
      </span>
    </label>
  );
}

function CountryField({ value, onChange }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-text-primary">Pays</span>
      <span className="mt-2 h-14 bg-white border border-gray-200 rounded-xl px-4 flex items-center gap-3 focus-within:border-accent-blue focus-within:ring-2 focus-within:ring-accent-blue/20">
        <MapPin className="w-5 h-5 text-text-muted" />
        <select value={value} onChange={(event) => onChange(event.target.value)} className="w-full bg-transparent outline-none text-text-primary">
          {countryOptions.map((country) => <option key={country.value} value={country.value}>{country.label}</option>)}
        </select>
      </span>
    </label>
  );
}
